import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useQueries, useQuery } from '@tanstack/react-query';
import type { Project } from 'api';
import { projectService, reportsService } from 'api';
import AddMembersDropdown from 'features/projects/components/AddMembersDropdown';
import { ProjectFormModal } from 'features/projects/components/ProjectFormModal';
import { useProjectActions } from 'features/projects/hooks/useProjectActions';
import { TrackedText } from 'features/projects/projects.styles';
import { MoreHorizontal } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from 'shared/components/ConfirmDialog';
import type { DataTableColumn } from 'shared/components/DataTable';
import { DataTable, TableScrollBox } from 'shared/components/DataTable';
import { queryKeys } from 'shared/constants/queryKeys';
import { ROUTE_PATHS } from 'shared/constants/routes';
import { usePermissions } from 'shared/hooks/usePermissions';

import {
  DETAIL_ACTIONS_MENU_ITEM_HOVER_SX,
  DETAIL_ACTIONS_MENU_ITEM_SX,
  DETAIL_ACTIONS_MENU_PAPER_SX,
  ClientProjectStatusDot,
  ClientProjectStatusPill,
  ClientTabContentStack,
  DetailActionsDeleteMenuItem,
  FilterSelect,
  FilterToolbarCard,
  ProjectRowActions,
  SearchFieldBox,
  SearchIconWrap,
  SearchInput,
  STATUS_MENU_ITEM_SX,
  STATUS_MENU_LIST_SX,
  STATUS_MENU_PAPER_SX,
} from '../clients.styles';

/** Matches the design's `formatHM` exactly (comma-grouped hours, zero-padded minutes) —
 * distinct from the shared `formatDurationShort` used elsewhere in the app, which doesn't
 * group large hour counts and isn't guaranteed zero-padded. Exported so the detail page's
 * hero "Billing Hours" KPI tile formats consistently with this table's "Total" column. */
export function formatTrackedHM(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  return `${h.toLocaleString('en-US')}h ${String(m).padStart(2, '0')}m`;
}

interface Props {
  clientId: string;
}

type StatusFilter = 'all' | 'Active' | 'Inactive' | 'OnHold';

interface RowMenuState {
  anchorEl: HTMLElement | null;
  projectId: string | null;
}

// Same "all time" range hack ProjectDetailPage uses for its own all-time stats,
// since the summary endpoint requires a bounded date range.
const ALL_TIME_FROM = '2000-01-01';

// Same query/mutations/modal the /projects page uses (useProjectActions) — Edit and
// Delete here are the identical flows, just scoped to this client's projects.
export const ClientProjectsTab: React.FC<Props> = ({ clientId }) => {
  const navigate = useNavigate();
  const { canEditProject, canDeleteProject, canAssignProjectMembers } = usePermissions();
  const [pendingStatusFilter, setPendingStatusFilter] = useState<StatusFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [rowMenu, setRowMenu] = useState<RowMenuState>({ anchorEl: null, projectId: null });
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const {
    projects,
    clients,
    projectModal,
    openEditModal,
    closeModal,
    handleModalSubmit,
    createMutation,
    updateMutation,
    deleteDialog,
    requestDelete,
    confirmDelete,
    cancelDelete,
    deleteMutation,
  } = useProjectActions();

  const clientProjects = useMemo(
    () => projects.filter((p) => p.clientId === clientId),
    [projects, clientId],
  );

  const filteredProjects = useMemo(() => {
    return clientProjects.filter((project) => {
      const matchesStatus = pendingStatusFilter === 'all' || project.status === pendingStatusFilter;
      const matchesSearch =
        !searchTerm || project.name.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  }, [clientProjects, pendingStatusFilter, searchTerm]);

  // Same per-project members endpoint ProjectAccessTab uses for its "Members" count and
  // Active/Inactive status badge — drives the "Mapped" column (total assigned members).
  const memberQueries = useQueries({
    queries: clientProjects.map((project) => ({
      queryKey: queryKeys.projects.members(project.id),
      queryFn: () => projectService.getProjectMembers(project.id).then((r) => r.data.data),
    })),
  });

  // Same query (same queryKey, same queryFn) ClientProjectDetailPage/
  // ClientProjectMembersSection use for their own "Active Contributors"/"Total" —
  // must stay byte-identical, not just same key: React Query dedupes purely by key,
  // so a different transform on the same key races with theirs, and whichever query
  // "wins" hands its own transformed shape (e.g. a Set) to the others. Every consumer
  // derives what it needs from this same raw groups array instead.
  //
  // "Active Contributors" matches the exact definition the Clients list page's
  // activeContributorCount column already uses: an active-membership member who has
  // logged at least one time entry — not just an active account.
  const contributorQueries = useQueries({
    queries: clientProjects.map((project) => ({
      queryKey: queryKeys.reports.summary({
        projectId: project.id,
        groupBy: 'user',
        from: ALL_TIME_FROM,
        to: today,
      }),
      queryFn: () =>
        reportsService
          .getSummary({ projectId: project.id, groupBy: 'user', from: ALL_TIME_FROM, to: today })
          .then((r) => r.data.data.groups),
    })),
  });

  const membersByProjectId = useMemo(() => {
    const map = new Map<string, { mapped: number; contributors: number }>();
    clientProjects.forEach((project, index) => {
      const members = memberQueries[index]?.data ?? [];
      const rawGroups = contributorQueries[index]?.data;
      const userIdsWithTime = new Set(
        (Array.isArray(rawGroups) ? rawGroups : [])
          .map((g) => g.key)
          .filter((k): k is string => Boolean(k)),
      );
      const contributors = members.filter(
        (m) =>
          m.workspaceMembershipStatus !== 'Inactive' &&
          m.projectMemberStatus !== 'Inactive' &&
          userIdsWithTime.has(m.userId),
      ).length;
      map.set(project.id, { mapped: members.length, contributors });
    });
    return map;
  }, [clientProjects, memberQueries, contributorQueries]);

  // Same query ProjectAccessTab uses to compute which members are already assigned,
  // scoped to whichever row's "Add member" popover is currently open.
  const { data: projectMembers = [] } = useQuery({
    queryKey: queryKeys.projects.members(rowMenu.projectId ?? ''),
    queryFn: () => projectService.getProjectMembers(rowMenu.projectId!).then((r) => r.data.data),
    enabled: Boolean(rowMenu.projectId) && addMemberOpen,
  });
  const assignedUserIds = useMemo(
    () => new Set(projectMembers.map((m) => m.userId)),
    [projectMembers],
  );

  const closeRowMenu = () => setRowMenu({ anchorEl: null, projectId: null });

  const handleEditClick = () => {
    if (rowMenu.projectId) openEditModal(rowMenu.projectId);
    closeRowMenu();
  };

  const handleAddMemberClick = () => {
    // Keep rowMenu's anchor + projectId set — the picker anchors to the same
    // element and needs the projectId for its members query — just close the menu itself.
    setAddMemberOpen(true);
  };

  const handleAddMemberOpenChange = (open: boolean) => {
    setAddMemberOpen(open);
    if (!open) closeRowMenu();
  };

  const handleDeleteClick = () => {
    if (rowMenu.projectId) requestDelete(rowMenu.projectId);
    closeRowMenu();
  };

  const showRowActions = canEditProject || canDeleteProject || canAssignProjectMembers;

  const columns: DataTableColumn<Project>[] = [
    // Column widths below match the design's exact grid template verbatim:
    // `grid-cols-[minmax(220px,1fr)_100px_120px_120px_110px_44px]` — "Project" is the
    // only flexible one (design's own screenshots show the same large gap after a short
    // project name at this container width; that's correct, not a bug). The design's
    // `gap-3` (12px) between every column is approximated via cellPaddingX below; the
    // extra inline padding on the first/last column's content restores the row's own
    // `px-5` (20px) outer edge, which cellPaddingX alone can't distinguish from the
    // inter-column gap.
    {
      id: 'name',
      label: (
        <Box component="span" sx={{ pl: '14px' }}>
          PROJECT
        </Box>
      ),
      render: (project) => (
        <Box component="span" sx={{ fontWeight: 600, fontSize: 13, pl: '14px' }}>
          {project.name}
        </Box>
      ),
    },
    {
      id: 'mapped',
      label: 'MAPPED',
      align: 'center',
      width: 100,
      render: (project) => membersByProjectId.get(project.id)?.mapped ?? 0,
    },
    {
      id: 'contributors',
      label: 'CONTRIBUTORS',
      align: 'center',
      width: 120,
      render: (project) => membersByProjectId.get(project.id)?.contributors ?? 0,
    },
    {
      // The design gives this column an extra `pr-8` (32px) on top of the row's normal
      // gap — on both the header and the value — specifically to create more breathing
      // room before "Status" than between the other columns.
      id: 'total',
      label: (
        <Box component="span" sx={{ pr: '32px' }}>
          TOTAL
        </Box>
      ),
      align: 'right',
      width: 120,
      render: (project) => (
        <TrackedText sx={{ whiteSpace: 'nowrap', pr: '32px' }}>
          {formatTrackedHM((project.trackedHours ?? 0) * 3600)}
        </TrackedText>
      ),
    },
    {
      id: 'status',
      label: showRowActions ? (
        'STATUS'
      ) : (
        <Box component="span" sx={{ pr: '14px' }}>
          STATUS
        </Box>
      ),
      width: 110,
      render: (project) => {
        const statusLabel =
          project.status === 'Active'
            ? 'Active'
            : project.status === 'OnHold'
              ? 'On Hold'
              : 'Inactive';
        const statusKey =
          project.status === 'Active'
            ? 'active'
            : project.status === 'OnHold'
              ? 'onhold'
              : 'archived';
        return (
          <Box
            component="span"
            sx={!showRowActions ? { display: 'inline-block', pr: '14px' } : undefined}
          >
            <ClientProjectStatusPill status={statusKey}>{statusLabel}</ClientProjectStatusPill>
          </Box>
        );
      },
    },
    ...(showRowActions
      ? [
          {
            id: 'actions',
            label: '',
            width: 44,
            align: 'right' as const,
            render: (project: Project) => (
              <ProjectRowActions sx={{ pr: '14px' }} active={rowMenu.projectId === project.id}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    // addMemberOpen is shared across rows (it controls one dropdown re-anchored
                    // per row), so it must be reset here — otherwise opening "…" on a different
                    // row while a previous row's Add Member picker was open just re-anchors that
                    // same picker to the new row instead of showing its own plain menu.
                    setAddMemberOpen(false);
                    setRowMenu({ anchorEl: e.currentTarget, projectId: project.id });
                  }}
                >
                  <MoreHorizontal size={14} />
                </IconButton>
              </ProjectRowActions>
            ),
          },
        ]
      : []),
  ];

  return (
    <ClientTabContentStack>
      <FilterToolbarCard sx={{ padding: '10px 12px', gap: 1 }}>
        <FilterSelect
          value={pendingStatusFilter}
          onChange={(e) => setPendingStatusFilter(e.target.value as StatusFilter)}
          IconComponent={KeyboardArrowDownIcon}
          sx={{
            width: 144,
            minWidth: 144,
            flexShrink: 0,
            '& .MuiSelect-select': {
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            },
          }}
          MenuProps={{
            PaperProps: { sx: STATUS_MENU_PAPER_SX },
            MenuListProps: { sx: STATUS_MENU_LIST_SX },
          }}
        >
          <MenuItem value="all" sx={STATUS_MENU_ITEM_SX}>
            All statuses
          </MenuItem>
          <MenuItem value="Active" sx={STATUS_MENU_ITEM_SX}>
            Active
          </MenuItem>
          <MenuItem value="Inactive" sx={STATUS_MENU_ITEM_SX}>
            Inactive
          </MenuItem>
          <MenuItem value="OnHold" sx={STATUS_MENU_ITEM_SX}>
            On Hold
          </MenuItem>
        </FilterSelect>

        <SearchFieldBox>
          <SearchIconWrap>
            <SearchIcon sx={{ fontSize: 16 }} />
          </SearchIconWrap>
          <SearchInput
            placeholder="Search project"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchFieldBox>
      </FilterToolbarCard>

      <TableScrollBox>
        <DataTable<Project>
          columns={columns}
          rows={filteredProjects}
          getRowId={(project) => project.id}
          onRowClick={(id) => navigate(ROUTE_PATHS.CLIENT_PROJECT_DETAIL(clientId, id))}
          emptyMessage={
            clientProjects.length === 0
              ? 'No projects are mapped to this client yet.'
              : 'No projects match your filters.'
          }
          borderless
          fixedLayout
          containerSx={{
            border: '1px solid rgba(34,28,90,0.07)',
            borderRadius: '14px',
            boxShadow: '0 1px 2px rgba(34,28,90,0.04), 0 8px 24px -8px rgba(34,28,90,0.06)',
          }}
          headerBgColor="#F3F0FF"
          headerColor="#5A5578"
          headerWeight={500}
          headerFontSize="10px"
          headerHeight={36}
          cellPaddingX={6}
          rowHeight={64}
          rowHoverColor="rgba(110, 79, 247, 0.03)"
        />
      </TableScrollBox>

      <Menu
        anchorEl={rowMenu.anchorEl}
        open={Boolean(rowMenu.anchorEl) && !addMemberOpen}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: DETAIL_ACTIONS_MENU_PAPER_SX } }}
        sx={{ '& .MuiList-root': { padding: 0 } }}
      >
        {canEditProject && (
          <MenuItem
            sx={{
              ...DETAIL_ACTIONS_MENU_ITEM_SX,
              ...DETAIL_ACTIONS_MENU_ITEM_HOVER_SX,
              color: '#141030',
            }}
            onClick={handleEditClick}
          >
            Edit project
          </MenuItem>
        )}
        {canAssignProjectMembers && (
          <MenuItem
            sx={{
              ...DETAIL_ACTIONS_MENU_ITEM_SX,
              ...DETAIL_ACTIONS_MENU_ITEM_HOVER_SX,
              color: '#141030',
            }}
            onClick={handleAddMemberClick}
          >
            Add member
          </MenuItem>
        )}
        {canDeleteProject && (canEditProject || canAssignProjectMembers) && (
          <Divider sx={{ mx: -0.5, my: 0.5, borderColor: 'rgba(34,28,90,0.07)' }} />
        )}
        {canDeleteProject && (
          <DetailActionsDeleteMenuItem sx={DETAIL_ACTIONS_MENU_ITEM_SX} onClick={handleDeleteClick}>
            Delete
          </DetailActionsDeleteMenuItem>
        )}
      </Menu>

      {/* Same AddMembersDropdown used on the Project Details page — controlled here so it
          opens from this row's "…" menu instead of its own built-in button. */}
      {rowMenu.projectId && (
        <AddMembersDropdown
          projectId={rowMenu.projectId}
          assignedUserIds={assignedUserIds}
          open={addMemberOpen}
          onOpenChange={handleAddMemberOpenChange}
          anchorEl={rowMenu.anchorEl}
          hideTrigger
        />
      )}

      <ConfirmDialog
        open={deleteDialog.open}
        title="Delete Project"
        message="Are you sure you want to delete this project? This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={() => confirmDelete()}
        onCancel={cancelDelete}
        loading={deleteMutation.isPending}
      />

      <ProjectFormModal
        open={projectModal.open}
        project={
          projectModal.projectId
            ? (projects.find((p) => p.id === projectModal.projectId) ?? null)
            : null
        }
        clients={clients}
        onClose={closeModal}
        onSubmit={handleModalSubmit}
        isPending={createMutation.isPending || updateMutation.isPending}
      />
    </ClientTabContentStack>
  );
};
